import React from 'react';
import { tv, type VariantProps } from 'tailwind-variants';

type PresetSize = 'sm' | 'md' | 'lg' | 'xl';
type Variant = 'ring' | 'dots' | 'pulse' | 'bars';

export interface SpinnerProps
    extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'>,
    VariantProps<typeof spinnerStyles> {
    size?: PresetSize | number;
    color?: string;
    overlay?: boolean;
    text?: string;
    position?: 'center' | 'inline';
}

function cn(...args: Array<string | false | null | undefined>) {
    return args.filter(Boolean).join(' ');
}

const spinnerStyles = tv({
    slots: {
        overlay: 'absolute inset-0 z-50 flex items-center justify-center rounded-[var(--radius-xl)] bg-[var(--bg-card)]/85 supports-[backdrop-filter]:backdrop-blur-md',
        stack: 'flex flex-col items-center gap-3.5',
        inline: 'inline-flex items-center gap-2.5 align-middle',
        text: 'font-semibold text-[var(--text-secondary)] tracking-[-0.01em]',
        ring: 'rounded-full border-[3px] border-t-transparent border-r-transparent animate-spin',
        dot: 'rounded-full',
        bar: 'rounded-full',
        pulse: 'rounded-full animate-ping',
    },
    variants: {
        variant: { ring: {}, dots: {}, pulse: {}, bars: {} },
        tone: {
            brand: '',
            neutral: '',
            success: '',
            warning: '',
            danger: '',
        },
        position: { center: '', inline: '' },
    },
    defaultVariants: { variant: 'ring', tone: 'brand', position: 'center' },
});

const sizeMap: Record<PresetSize, number> = { sm: 16, md: 24, lg: 32, xl: 48 };

const DEFAULT_BRAND_COLOR = 'var(--primary-600, #045eb8)';

const Spinner: React.FC<SpinnerProps> = ({
    size = 'md',
    variant = 'ring',
    tone = 'brand',
    color,
    overlay = true,
    text,
    position = 'center',
    className,
    ...rest
}) => {
    const px = typeof size === 'number' ? size : sizeMap[size];

    const { overlay: overlayCls, stack, inline, text: textCls, ring, dot, bar, pulse } =
        spinnerStyles({ variant, tone, position });

    const fg = color || DEFAULT_BRAND_COLOR;
    const renderVisual = () => {
        switch (variant) {
            case 'dots':
                return (
                    <div className="flex items-center gap-1" aria-hidden>
                        {[0, 1, 2].map(i => (
                            <span
                                key={i}
                                className={dot()}
                                style={{
                                    width: px / 4,
                                    height: px / 4,
                                    backgroundColor: fg,
                                    animation: 'pulse 0.9s ease-in-out infinite',
                                    animationDelay: `${i * 0.12}s`,
                                }}
                            />
                        ))}
                    </div>
                );

            case 'bars':
                return (
                    <div className="flex items-end gap-1" aria-hidden>
                        {[0, 1, 2, 3].map(i => (
                            <span
                                key={i}
                                className={bar()}
                                style={{
                                    width: Math.max(2, Math.round(px / 8)),
                                    height: px,
                                    backgroundColor: fg, // background (not text) controls color
                                    animation: 'pulse 1s ease-in-out infinite',
                                    animationDelay: `${i * 0.08}s`,
                                }}
                            />
                        ))}
                    </div>
                );
            case 'pulse':
                return (
                    <span
                        className={pulse()}
                        aria-hidden
                        style={{
                            width: px,
                            height: px,
                            backgroundColor: fg, // background (not text) controls color
                        }}
                    />
                );

            case 'ring':
            default:
                return (
                    <span
                        className={ring()}
                        aria-hidden
                        style={{
                            width: px,
                            height: px,
                            borderLeftColor: fg,
                            borderBottomColor: fg,
                        }}
                    />
                );
        }
    };

    const body =
        position === 'inline' ? (
            <div className={cn(inline(), className)} role="status" aria-live="polite" {...rest}>
                {renderVisual()}
                {text && <span className={cn(textCls(), 'text-sm')}>{text}</span>}
            </div>
        ) : (
            <div className={cn(stack(), className)} role="status" aria-live="polite" {...rest}>
                {renderVisual()}
                {text && <p className={cn(textCls(), px < 24 ? 'text-sm' : 'text-base')}>{text}</p>}
            </div>
        );

    if (!overlay || position === 'inline') return body;
    return <div className={overlayCls()}>{body}</div>;
};

export default Spinner;
